// resources/js/admin/components/shared/locales/LocaleForm.tsx

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Save } from "lucide-react";
import { Locale, LocaleFormData } from "../../../types/features/Locale";
import useLocales from "../../../hooks/features/locales/useLocales";

interface LocaleFormProps {
    initialData?: Locale;
    isEdit?: boolean;
    onSubmit: (data: LocaleFormData) => Promise<void>;
    isLoading?: boolean;
    isSuccess?: boolean;
}

export default function LocaleForm({
    initialData,
    isEdit = false,
    onSubmit,
    isLoading = false,
    isSuccess = false,
}: LocaleFormProps) {
    const navigate = useNavigate();
    const { locales } = useLocales();
    const [formData, setFormData] = useState<LocaleFormData>({
        code: "",
        name: "",
        flag: "",
        is_active: true,
        is_rtl: false,
        fallback: "",
    });

    useEffect(() => {
        if (initialData) {
            setFormData({
                code: initialData.code,
                name: initialData.name,
                flag: initialData.flag || "",
                is_active: initialData.is_active,
                is_rtl: initialData.is_rtl,
                fallback: initialData.fallback || "",
            });
        }
    }, [initialData]);

    useEffect(() => {
        if (isSuccess) {
            setFormData({
                code: "",
                name: "",
                flag: "",
                is_active: true,
                is_rtl: false,
                fallback: "",
            });
        }
    }, [isSuccess]);

    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
    ) => {
        const { name, value, type } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? (e.target as HTMLInputElement).checked : value,
        }));
    };

    const handleCheckbox = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, checked } = e.target;
        setFormData((prev) => ({ ...prev, [name]: checked }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        await onSubmit(formData);
    };

    // Liste des langues disponibles pour le fallback (exclure la langue en cours)
    const availableFallbacks = locales?.filter((l) => l.code !== formData.code) || [];

    return (
        <form onSubmit={handleSubmit} className="space-y-6">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                {/* Informations générales */}
                <div className="mb-6">
                    <div className="border-b border-gray-200 mb-4 pb-2">
                        <h2 className="text-lg font-semibold text-gray-900">Informations générales</h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Code <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                name="code"
                                value={formData.code}
                                onChange={handleChange}
                                required
                                disabled={isEdit}
                                className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary ${
                                    isEdit ? "bg-gray-100 cursor-not-allowed" : ""
                                }`}
                                placeholder="fr"
                            />
                            {isEdit && (
                                <p className="text-xs text-gray-500 mt-1">Le code ne peut pas être modifié</p>
                            )}
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Nom <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                required
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary"
                                placeholder="Français"
                            />
                        </div>
                    </div>
                </div>

                {/* Apparence */}
                <div className="mb-6">
                    <div className="border-b border-gray-200 mb-4 pb-2">
                        <h2 className="text-lg font-semibold text-gray-900">Apparence</h2>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Drapeau (emoji)
                        </label>
                        <input
                            type="text"
                            name="flag"
                            value={formData.flag}
                            onChange={handleChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary"
                            placeholder="🇫🇷"
                        />
                        <p className="text-xs text-gray-500 mt-1">Exemple: 🇫🇷, 🇬🇧, 🇪🇸</p>
                    </div>
                </div>

                {/* Configuration */}
                <div className="mb-6">
                    <div className="border-b border-gray-200 mb-4 pb-2">
                        <h2 className="text-lg font-semibold text-gray-900">Configuration</h2>
                    </div>
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Langue de fallback
                            </label>
                            <select
                                name="fallback"
                                value={formData.fallback}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary"
                            >
                                <option value="">Aucune</option>
                                {availableFallbacks.map((locale) => (
                                    <option key={locale.code} value={locale.code}>
                                        {locale.flag} {locale.name} ({locale.code.toUpperCase()})
                                    </option>
                                ))}
                            </select>
                            <p className="text-xs text-gray-500 mt-1">
                                Langue utilisée si le contenu n'est pas disponible dans cette langue
                            </p>
                        </div>

                        <div className="flex items-center gap-2">
                            <input
                                type="checkbox"
                                name="is_rtl"
                                checked={formData.is_rtl}
                                onChange={handleCheckbox}
                                className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
                            />
                            <label className="text-sm font-medium text-gray-700">
                                Écriture de droite à gauche (RTL)
                            </label>
                        </div>

                        <div className="flex items-center gap-2">
                            <input
                                type="checkbox"
                                name="is_active"
                                checked={formData.is_active}
                                onChange={handleCheckbox}
                                className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
                            />
                            <label className="text-sm font-medium text-gray-700">Langue active</label>
                        </div>
                    </div>
                </div>
            </div>

            <div className="flex justify-end gap-3">
                <button
                    type="button"
                    onClick={() => navigate(-1)}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                    Annuler
                </button>
                <button
                    type="submit"
                    disabled={isLoading}
                    className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50 transition-colors"
                >
                    <Save className="w-4 h-4" />
                    {isLoading ? "Enregistrement..." : isEdit ? "Mettre à jour" : "Créer"}
                </button>
            </div>
        </form>
    );
}